import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { useRoutes, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSnapshot } from 'valtio';
import { useLoading } from './LoadingProvider';
import { sessionState } from './sessionState';
import { fetchData } from '@/components/data/fetchData';
import { qkeyFromUrl } from '@/utils';
import config from '@/app/config';

import CodingAgent from '../editor/CodingAgent';
import DefaultPage from '../pages/default';
import NotFound from './NotFound';

import LayoutEmbed from './LayoutEmbed';
import LayoutWebpart from './LayoutWebpart';
import LayoutApp from './LayoutApp';
import LayoutGadget from './LayoutGadget';
import LayoutAdmin from './LayoutAdmin';
import LayoutNone from './LayoutNone';

import './App.css'

import routes from '~react-pages';

// Custom loading component that integrates with the centralized loading state
const SuspenseFallback = () => {
  const { startLoading, stopLoading } = useLoading();

  // Using useLayoutEffect ensures the loading state is set
  // before the browser paints anything to the screen
  React.useLayoutEffect(() => {
    // Start loading immediately
    const loaderId = startLoading();

    // Clean up when suspense resolves
    return () => {
      stopLoading(loaderId);
    };
  }, [startLoading, stopLoading]);

  // Return an empty, invisible element
  return <div style={{ display: 'none' }} aria-hidden="true"></div>;
  //return <div>XXXLoading...</div>;
}

function App() {
  const { startLoading, stopLoading } = useLoading();
  const location = useLocation();
  const navigationType = useNavigationType();
  const sessionSnapshot = useSnapshot(sessionState);
  const sessionIsLoading = sessionSnapshot.isLoading;

  // Get URL parameters
  // Get query parameters (from ?key=value)
  const [searchParams] = useSearchParams();

  // Check if we're on an embed route
  let isEmbedRoute = location.pathname.startsWith('/embed/') || location.pathname.startsWith('/webpart/');
  let url = "";
  let queryKey = [];
  const cardId = searchParams.get('cardId');
  const token = searchParams.get('token');

  if (cardId && token) {
    url = `${config.API_URL}/api/card/endpoint/${cardId}`;
    queryKey = [url];
  } else {
    isEmbedRoute = false; // Disable embed mode if no cardId is present
  }

  // Fetch data using React Query only for embed routes
  const { data: fetchedData, isLoading, isError, error } = useQuery({
    queryKey: queryKey,
    queryFn: (context) => fetchData(url, context),
    meta: { token },
    enabled: isEmbedRoute && !sessionIsLoading, // Only fetch if on embed route after session is loaded
    staleTime: 30 * 1000, // Consider data fresh for 10 seconds
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Merge fetched data with search params
  const mergedParams = useMemo(() => {
    const searchParamsObj = Object.fromEntries(searchParams);

    // For embed routes with fetched data, merge them
    if (isEmbedRoute && fetchedData) {

      let props = fetchedData || {};
      delete props._hash;
      delete props.payload;

      if (window.location.pathname.startsWith("/embed/rss-")) delete props.url;  // v1 rss artifacts use url for sample data, may not be replaced !

      //console.log('Merged params for embed route:', props, searchParamsObj);

      // Search params take precedence over fetched data
      return {
        ...props,
        ...searchParamsObj
      };
    }

    // For non-embed routes, just use search params
    return searchParamsObj;
  }, [fetchedData, searchParams, isEmbedRoute]);

  // Handle loading state for React Query on embed routes
  useEffect(() => {
    if (isEmbedRoute && isLoading) {
      const loaderId = startLoading();
      return () => stopLoading(loaderId);
    }
  }, [isLoading, isEmbedRoute, startLoading, stopLoading]);

  // let isLoading = false, isError = false, mergedParams = searchParams;

  // Start loading on route changes
  useEffect(() => {
    // Only create a loader for fresh navigation, not for back/forward
    if (navigationType === 'PUSH') {
      const loaderId = startLoading();

      // Use a small timeout to allow for immediate renders
      const timeoutId = setTimeout(() => {
        stopLoading(loaderId);
      }, 200);

      return () => {
        clearTimeout(timeoutId);
        stopLoading(loaderId);
      };
    }
  }, [location.pathname, navigationType, startLoading, stopLoading]);

  const layouts = {
    app: LayoutApp,
    admin: LayoutAdmin,
    embed: LayoutEmbed,
    webpart: LayoutWebpart,
    gadget: LayoutGadget,
    none: LayoutNone
  };

  let element = useRoutes([
    ...routes,
    {
      path: "*",
      element: <NotFound />
    }
  ]);

  const layout = element?.props?.match?.route?.meta?.layout || 'none';
  var Layout = layouts[layout] || LayoutNone;

  // We use cloneElement to add the merged parameters as props to the rendered element
  // This way, any page component will receive merged parameters as props
  // Don't wait for loading on non-embed routes

  if (location.pathname !== '/auth-callback' && (!isEmbedRoute || !isLoading)) {
    if (element && Object.keys(mergedParams).length > 0) {
      // We need to update the children element with the merged params
      let updatedChildren = React.cloneElement(element.props.children, mergedParams);

      // Then clone the main element, replacing its children with our updated children
      element = React.cloneElement(element, {
        children: updatedChildren
      });
    }
  }

  // Replace root route element
  if (location.pathname === '/') {
    if (import.meta.env.VITE_DISABLE_AGENT) {
      element = <DefaultPage />;
    } else {
      element = <CodingAgent />;
      Layout = LayoutNone;
    }
  }

  // Show loading state only for embed routes while fetching initial data
  if (isEmbedRoute && isLoading) {
    return <SuspenseFallback />;
  }

  // Handle error state gracefully - still render with just search params
  if (isEmbedRoute && isError) {
    console.error('Failed to fetch additional parameters from API for embed route');
  }

  return (
    <Layout {...mergedParams}>
      <Suspense fallback={<SuspenseFallback />}>
        {element}
      </Suspense>
    </Layout>
  );
}

export default App;